import { socketService } from "./socket";

export interface PendingLog {
  id: string;
  data: any;
  timestamp: number;
  retries: number;
}

const STORAGE_KEY = "braindog_pending_logs";
const MAX_PENDING = 500;
const ACK_TIMEOUT_MS = 10_000;
const MAX_RETRIES = 5;

class LogBuffer {
  private pending: PendingLog[] = [];
  private flushing = false;
  private _onChangeCallbacks: Array<(pending: PendingLog[]) => void> = [];

  constructor() {
    this.loadFromStorage();

    // Auto-flush when socket reconnects
    socketService.onConnectionChange((connected) => {
      if (connected && this.pending.length > 0) {
        console.info(`📦 [LogBuffer] Reconnected — flushing ${this.pending.length} pending logs`);
        this.flush();
      }
    });
  }

  /** Add a log to the buffer and try to send it */
  add(logData: any): void {
    // Propagate the content clock as `_startAt` for the server's
    // `new-log` handler (server/events/story-events.js sets
    // `start_at = logEntry._startAt || now`). `createdAt` remains a backwards-
    // compatible fallback for pending logs created before startAt existed.
    const sourceStartAt = logData.startAt ?? logData.createdAt;
    const startAtIso =
      typeof sourceStartAt === "number"
        ? new Date(sourceStartAt).toISOString()
        : typeof sourceStartAt === "string"
          ? sourceStartAt
          : undefined;

    const entry: PendingLog = {
      id: logData.id || `LOG_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      data: {
        ...logData,
        ...(startAtIso ? { _startAt: startAtIso } : {}),
        _bufferedAt: Date.now(),
      },
      timestamp: Date.now(),
      retries: 0,
    };

    // Ensure the log data has the ID
    entry.data.id = entry.id;

    this.pending.push(entry);

    // Enforce max size
    if (this.pending.length > MAX_PENDING) {
      this.pending = this.pending.slice(-MAX_PENDING);
    }

    this.saveToStorage();
    this.notifyChange();

    // Try to send immediately
    if (socketService.connected) {
      this.flush();
    } else {
      console.warn(
        `📦 [LogBuffer] Offline — log queued: ${entry.id} (${this.pending.length} pending)`,
      );
    }
  }

  /** Flush all pending logs to the server, one by one in order */
  async flush(): Promise<void> {
    if (this.flushing || this.pending.length === 0) return;
    this.flushing = true;

    console.info(`📦 [LogBuffer] Flushing ${this.pending.length} pending logs...`);

    // Process one at a time to maintain order
    while (this.pending.length > 0 && socketService.connected) {
      const entry = this.pending[0];

      try {
        const ack = await this.sendWithAck(entry);
        if (ack?.success) {
          // Log confirmed by server — remove from queue
          this.pending.shift();
          this.saveToStorage();
          this.notifyChange();
          console.info(`✅ [LogBuffer] ACK received for ${entry.id}`);
        } else {
          // Server rejected (e.g. duplicate) — still remove
          console.warn(`⚠️ [LogBuffer] Server rejected ${entry.id}: ${ack?.error || "unknown"}`);
          this.pending.shift();
          this.saveToStorage();
          this.notifyChange();
        }
      } catch (err) {
        // Timeout or connection lost — stop flushing, retry later
        entry.retries++;
        if (entry.retries >= MAX_RETRIES) {
          console.error(`❌ [LogBuffer] Max retries for ${entry.id} — dropping`);
          this.pending.shift();
          this.saveToStorage();
          this.notifyChange();
        } else {
          console.warn(
            `📦 [LogBuffer] Send failed for ${entry.id}, retry ${entry.retries}/${MAX_RETRIES}`,
          );
          break; // Stop flushing, will retry on next connect
        }
      }
    }

    this.flushing = false;
  }

  /** Send a log with ACK (callback-based acknowledgement) */
  private sendWithAck(entry: PendingLog): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("ACK timeout"));
      }, ACK_TIMEOUT_MS);

      socketService.emitWithAck("new-log", entry.data, (response: any) => {
        clearTimeout(timeout);
        resolve(response || { success: true });
      });
    });
  }

  /** Get current pending logs count */
  get pendingCount(): number {
    return this.pending.length;
  }

  /** Get pending logs (read-only) */
  getPending(): readonly PendingLog[] {
    return this.pending;
  }

  /** Subscribe to changes */
  onChange(callback: (pending: PendingLog[]) => void): () => void {
    this._onChangeCallbacks.push(callback);
    return () => {
      this._onChangeCallbacks = this._onChangeCallbacks.filter((cb) => cb !== callback);
    };
  }

  private notifyChange(): void {
    this._onChangeCallbacks.forEach((cb) => cb([...this.pending]));
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.pending = JSON.parse(stored);
        if (this.pending.length > 0) {
          console.info(`📦 [LogBuffer] Loaded ${this.pending.length} pending logs from storage`);
        }
      }
    } catch {
      this.pending = [];
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.pending));
    } catch (e) {
      console.error("📦 [LogBuffer] Failed to save to localStorage:", e);
    }
  }
}

export const logBuffer = new LogBuffer();
